import * as React from "react";
import {
  MenuPopover,
  MenuPopoverTrigger,
  MenuPopoverContent,
  MenuPopoverItem,
  MenuPopoverSeparator,
  MenuPopoverLabel,
} from "src/sudokit/components/MenuPopover";

type Side = "bottom" | "top" | "right" | "left";
type Align = "start" | "center" | "end";

interface SudoMenuButtonProps {
  /** The trigger element — an IconButton, SudoButton, or any forwardRef component */
  trigger: React.ReactElement;
  /** Menu content (MenuPopoverItems, custom JSX, etc.) */
  children: React.ReactNode;

  // MenuPopover props
  /** Controlled open state */
  open?: boolean;
  /** Callback when open state changes */
  onOpenChange?: (open: boolean) => void;
  /** Whether popover is modal (default true) */
  modal?: boolean;

  // MenuPopoverContent props
  /** Popover placement side (default "bottom") */
  side?: Side;
  /** Popover alignment (default "start") */
  align?: Align;
  /** Offset from the trigger along the side axis (default 5) */
  sideOffset?: number;
  /** Offset along the alignment axis (default 0) */
  alignOffset?: number;
  /** Collision padding */
  collisionPadding?: number | Partial<Record<Side, number>>;
  /** Accessible label for the menu content region */
  menuAriaLabel?: string;
  /** Additional className on the positioned menu content */
  menuClassName?: string;
  /** Additional className on the outer portal wrapper. Use for stacking context overrides only. */
  menuPortalClassName?: string;
  /** Additional className on the inner menu content div (has p-2 by default) */
  contentClassName?: string;
  /** Whether the content has entry/exit animation (default true) */
  animated?: boolean;
  /** Show a Radix Popover.Arrow pointing at the trigger (default false) */
  showArrow?: boolean;
  /** Custom className for the arrow */
  arrowClassName?: string;
}

const SudoMenuButton = React.forwardRef<HTMLElement, SudoMenuButtonProps>(
  (
    {
      trigger,
      children,
      open,
      onOpenChange,
      modal,
      side = "bottom",
      align = "start",
      sideOffset = 10,
      alignOffset = 0,
      collisionPadding,
      menuAriaLabel,
      menuClassName,
      menuPortalClassName,
      contentClassName,
      animated,
      showArrow,
      arrowClassName,
    },
    ref,
  ) => {
    // Clone the trigger to forward the ref for Radix positioning and focus return
    const triggerWithRef = React.cloneElement(trigger, { ref });

    return (
      <MenuPopover open={open} onOpenChange={onOpenChange} modal={modal}>
        <MenuPopoverTrigger>{triggerWithRef}</MenuPopoverTrigger>
        <MenuPopoverContent
          side={side}
          align={align}
          sideOffset={sideOffset}
          alignOffset={alignOffset}
          collisionPadding={collisionPadding}
          aria-label={menuAriaLabel}
          className={menuClassName}
          portalClassName={menuPortalClassName}
          contentClassName={contentClassName}
          animated={animated}
          showArrow={showArrow}
          arrowClassName={arrowClassName}
        >
          {children}
        </MenuPopoverContent>
      </MenuPopover>
    );
  },
);

SudoMenuButton.displayName = "SudoMenuButton";

export { SudoMenuButton, MenuPopoverItem, MenuPopoverSeparator, MenuPopoverLabel };
export type { SudoMenuButtonProps };
